import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/use-toast";
import { AlertTriangle, Info, Mail, CheckCircle, Eye, EyeOff, Home, HelpCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import CustomSignupCheckout from "@/components/auth/CustomSignupCheckout";
import { firstChargeLabel, monthlyLabel, signupPricingSentence } from "@/lib/pricing";
import { hidePaywallUI } from "@/lib/platform";

// On iOS, Apple's IAP rules forbid selling subscriptions outside IAP, so we
// hide all paywall surfaces. Users sign up & subscribe on the website.
const HIDE_PAYWALL = hidePaywallUI();

const SECURITY_QUESTIONS = [
  "What was the name of your first pet?",
  "What is your mother's maiden name?",
  "What city were you born in?",
  "What was the name of your first school?",
  "What is your favourite book?",
];

const AUTH_REDIRECT_ORIGIN = "https://houndpayments.co.uk";
const getAuthRedirectUrl = (search: string) => `${AUTH_REDIRECT_ORIGIN}/auth?${search}`;
const isSupabaseEmailOtpType = (type: string | null): type is "signup" | "email" | "recovery" =>
  type === "signup" || type === "email" || type === "recovery";

const Auth = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [signupComplete, setSignupComplete] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [activeTab, setActiveTab] = useState("signin");
  const [isPasswordUpdate, setIsPasswordUpdate] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // Security question state
  const [securityQuestion, setSecurityQuestion] = useState(SECURITY_QUESTIONS[0]);
  const [customQuestion, setCustomQuestion] = useState("");
  const [securityAnswer, setSecurityAnswer] = useState("");
  // Reset flow state
  const [resetStep, setResetStep] = useState<"email" | "answer">("email");
  const [resetQuestion, setResetQuestion] = useState<string | null>(null);
  const [resetAnswer, setResetAnswer] = useState("");
  // Promo code state
  const [promoInput, setPromoInput] = useState("");
  const [promoApplied, setPromoApplied] = useState<{ code: string; description: string; isFullDiscount?: boolean } | null>(null);
  const [promoChecking, setPromoChecking] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const signupInProgressRef = useRef(false);
  const authEmailLinkInProgressRef = useRef(false);

  // Parse hash fragments and convert to query parameters
  const parseUrlParams = () => {
    const hash = window.location.hash.substring(1);
    const search = window.location.search.substring(1);
    
    // Parse both hash and query parameters
    const hashParams = new URLSearchParams(hash);
    const queryParams = new URLSearchParams(search);
    
    // Combine both sets of parameters
    const allParams = new URLSearchParams();
    for (const [key, value] of hashParams) {
      allParams.set(key, value);
    }
    for (const [key, value] of queryParams) {
      allParams.set(key, value);
    }
    
    return allParams;
  };

  useEffect(() => {
    console.log("Auth component mounted, checking URL parameters");
    
    // Parse URL parameters from both hash and query
    const params = parseUrlParams();
    const tab = params.get("tab");
    const type = params.get("type");
    const tokenHash = params.get("token_hash");
    const accessToken = params.get("access_token");
    const refreshToken = params.get("refresh_token");
    const error = params.get("error");
    const errorDescription = params.get("error_description");
    
    console.log("URL params:", { tab, type, tokenHash: !!tokenHash, accessToken: !!accessToken, refreshToken: !!refreshToken, error, errorDescription });
    
    // Handle token-hash links from branded auth email templates. These links go
    // straight to houndpayments.co.uk and avoid Supabase falling back to any old Site URL.
    if (tokenHash && isSupabaseEmailOtpType(type)) {
      authEmailLinkInProgressRef.current = true;
      const verifyEmailLink = async () => {
        const isRecovery = type === "recovery";
        if (isRecovery) {
          setIsPasswordUpdate(true);
          setActiveTab("update-password");
        }

        const { data: verifyData, error: verifyError } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type,
        });

        if (verifyError) {
          toast({
            title: "Authentication Error",
            description: "This email link is invalid or has expired. Please request a new one.",
            variant: "destructive",
          });
          authEmailLinkInProgressRef.current = false;
          window.history.replaceState({}, document.title, isRecovery ? "/auth?tab=reset" : "/auth?tab=signin");
          return;
        }

        if (isRecovery) {
          // Ensure the recovery session is persisted before the user submits a new password.
          if (verifyData?.session) {
            await supabase.auth.setSession({
              access_token: verifyData.session.access_token,
              refresh_token: verifyData.session.refresh_token,
            });
          }
          const { data: { session: confirmedSession } } = await supabase.auth.getSession();
          console.log("Recovery session established:", !!confirmedSession);
          toast({
            title: "Ready to update password",
            description: "Please enter your new password below.",
          });
          window.history.replaceState({}, document.title, "/auth?tab=update-password");
          return;
        }

        await supabase.auth.signOut();
        authEmailLinkInProgressRef.current = false;
        toast({
          title: "Email confirmed",
          description: "Your email has been confirmed. Please sign in to continue.",
        });
        setActiveTab("signin");
        window.history.replaceState({}, document.title, "/auth?tab=signin");
      };

      verifyEmailLink();
      return;
    }
    
    // Handle errors from password reset
    if (error) {
      console.error("Auth error from URL:", error, errorDescription);
      toast({
        title: "Authentication Error",
        description: errorDescription || error,
        variant: "destructive"
      });
      // Clean up the URL
      window.history.replaceState({}, document.title, "/auth?tab=reset");
      return;
    }
    
    if (type === "signup" || params.get("confirmed") === "1") {
      toast({
        title: "Email confirmed",
        description: "Your email has been confirmed. Please sign in to continue.",
      });
      setActiveTab("signin");
      window.history.replaceState({}, document.title, "/auth?tab=signin");
      return;
    }

    // Handle password recovery from email link
    if (type === "recovery" || (accessToken && tab === "update-password")) {
      console.log("Password recovery detected");
      setIsPasswordUpdate(true);
      setActiveTab("update-password");
      
      // If we have tokens, set the session
      if (accessToken && refreshToken) {
        console.log("Setting session from URL tokens");
        supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken
        }).then(({ data, error }) => {
          if (error) {
            console.error("Error setting session:", error);
            toast({
              title: "Error",
              description: "Invalid or expired reset link. Please request a new password reset.",
              variant: "destructive"
            });
          } else {
            console.log("Session set successfully for password update");
            toast({
              title: "Ready to update password",
              description: "Please enter your new password below."
            });
          }
        });
      }
      
      // Clean up the URL
      window.history.replaceState({}, document.title, "/auth?tab=update-password");
      return;
    }
    
    // Handle return from Stripe checkout
    if (params.get("subscription") === "success") {
      const sessionId = params.get("session_id");
      // Fire-and-forget: sync billing to Stripe customer & send activation email
      if (sessionId) {
        supabase.functions
          .invoke("signup-activation", { body: { session_id: sessionId } })
          .catch((e) => console.error("signup-activation failed:", e));
      }
      // Make sure we don't keep a half-authenticated session around
      supabase.auth.signOut().catch(() => {});
      toast({
        title: "Payment confirmed",
        description:
          "Check your email to activate your account, then sign in to continue.",
      });
      setActiveTab("signin");
      window.history.replaceState({}, document.title, "/auth?tab=signin");
      return;
    }

    // Handle other tab navigation
    if (tab === "signup") {
      navigate("/signup", { replace: true });
      return;
    }
    if (tab === "signin" || tab === "reset") {
      setActiveTab(tab);
    }
  }, [location]);

  useEffect(() => {
    console.log("Setting up auth state listener");
    
    // Check if user is already signed in
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log("Current session:", !!session);
      if (session && !isPasswordUpdate && !authEmailLinkInProgressRef.current) {
        console.log("User already signed in, redirecting to dashboard");
        navigate("/");
      }
    });

    // Set up auth listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log("Auth event:", event, "Session:", !!session, "User email:", session?.user?.email);
      
      if (session && !isPasswordUpdate && !authEmailLinkInProgressRef.current) {
        if (event === "SIGNED_IN") {
          console.log("User signed in successfully");
          if (!signupInProgressRef.current) {
            toast({ title: "Welcome!", description: "You've successfully signed in." });
            navigate("/");
          }
        } else if (event === "USER_UPDATED" && session?.user) {
          console.log("User updated event");
          navigate("/");
        }
      }
      
      if (event === "PASSWORD_RECOVERY") {
        console.log("Password recovery event triggered");
        setIsPasswordUpdate(true);
        setActiveTab("update-password");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, isPasswordUpdate]);

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    // Suppress the "Welcome!" toast + auto-navigate from the auth listener
    // until we've verified the subscription. Cleared on success below.
    signupInProgressRef.current = true;

    try {
      console.log("Attempting sign in for:", email);

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error("Sign in error:", error);
        if (error.message.includes("Invalid login credentials")) {
          toast({ 
            title: "Invalid credentials", 
            description: "Please check your email and password and try again.", 
            variant: "destructive" 
          });
        } else {
          toast({ 
            title: "Error signing in", 
            description: error.message, 
            variant: "destructive" 
          });
        }
        return;
      }

      // Gate access on an active/trialing Stripe subscription.
      // Skip on iOS: Apple forbids selling subs in-app, so the iOS app is
      // sign-in only and trusts the web-side subscription state.
      if (!HIDE_PAYWALL) {
        try {
          const { data: subData, error: subErr } = await supabase.functions.invoke("check-subscription");
          if (subErr) throw subErr;
          if (!subData?.subscribed) {
            // No active subscription — sign back out and surface checkout.
            // The embedded checkout lives in the signup tab, so switch tabs
            // so the user actually sees the card form instead of a blank signin.
            await supabase.auth.signOut();
            signupInProgressRef.current = true;
            setActiveTab("signup");
            setShowCheckout(true);
            toast({
              title: "Finish your subscription",
              description: "Your account exists but payment wasn't completed. Enter your card below to activate it.",
              variant: "destructive",
            });
            return;
          }
        } catch (subCheckErr: any) {
          console.error("Subscription check failed:", subCheckErr);
          await supabase.auth.signOut();
          toast({
            title: "Could not verify subscription",
            description: "Please try again in a moment.",
            variant: "destructive",
          });
          return;
        }
      }

      // Subscription verified — release the suppression and navigate.
      signupInProgressRef.current = false;
      console.log("Sign in successful");
      toast({ title: "Welcome!", description: "You've successfully signed in." });
      navigate("/");
    } catch (error: any) {
      console.error("Sign in exception:", error);
      toast({ 
        title: "Error signing in", 
        description: error.message || "An unexpected error occurred", 
        variant: "destructive" 
      });
    } finally {
      setLoading(false);
    }
  };


  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast({
        title: "Passwords do not match",
        description: "Please make sure both passwords are identical.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    signupInProgressRef.current = true;
    let keepSignupFlowOpen = false;

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: getAuthRedirectUrl("tab=signin&confirmed=1"),
        },
      });

      if (error) {
        if (error.message.includes("User already registered")) {
          toast({
            title: "Account exists",
            description: "An account with this email already exists. Please sign in instead.",
            variant: "destructive",
          });
          setActiveTab("signin");
        } else {
          toast({ title: "Error creating account", description: error.message, variant: "destructive" });
        }
        return;
      }

      if (data.user) {
        keepSignupFlowOpen = true;
        setShowCheckout(true);
        if (data.session) {
          toast({
            title: "Account created!",
            description: `Enter your card below to start your ${monthlyLabel()}/month subscription.`,
            variant: "success",
          });
        } else {
          toast({
            title: "Account created!",
            description: `Enter your card below to start your ${monthlyLabel()}/month subscription.`,
            variant: "success",
          });
        }
      }
    } catch (error: any) {
      toast({
        title: "Error creating account",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      if (!keepSignupFlowOpen) {
        signupInProgressRef.current = false;
      }
    }
  };

  const handleResetLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("security-question", {
        body: { action: "lookup", email },
      });
      if (error) throw error;

      if (data?.hasQuestion) {
        setResetQuestion(data.question);
        setResetStep("answer");
      } else {
        // Fallback: no security question on file -> send branded reset email via Resend
        const { error: resetErr } = await supabase.functions.invoke("send-password-reset", {
          body: {
            email,
            redirectTo: getAuthRedirectUrl("tab=update-password&type=recovery"),
          },
        });
        if (resetErr) throw resetErr;
        setResetSent(true);
        toast({
          title: "Reset email sent",
          description: "Please check your email for password reset instructions.",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Could not start password reset.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("security-question", {
        body: {
          action: "verify_and_reset",
          email,
          answer: resetAnswer,
          redirectTo: getAuthRedirectUrl("tab=update-password&type=recovery"),
        },
      });
      if (error || !data?.ok) {
        toast({
          title: "Verification failed",
          description: "The answer you entered is incorrect.",
          variant: "destructive",
        });
        return;
      }
      const { error: resetErr } = await supabase.functions.invoke("send-password-reset", {
        body: {
          email,
          redirectTo: getAuthRedirectUrl("tab=update-password&type=recovery"),
        },
      });
      if (resetErr) throw resetErr;
      setResetSent(true);
      toast({
        title: "Reset email sent",
        description: "Please check your email for password reset instructions.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Could not verify your answer.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure both password fields match.",
        variant: "destructive"
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        title: "Password too short",
        description: "Password must be at least 6 characters long.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    
    try {
      console.log("Attempting password update");

      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (!currentSession) {
        throw new Error(
          "Your password reset session has expired. Please request a new reset email and try again.",
        );
      }

      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        console.error("Password update error:", error);
        throw error;
      }

      console.log("Password updated successfully");
      toast({
        title: "Password updated",
        description: "Your password has been successfully updated. You can now sign in."
      });
      
      setIsPasswordUpdate(false);
      authEmailLinkInProgressRef.current = false;
      setActiveTab("signin");
      setNewPassword("");
      setConfirmPassword("");
      
      // Clean up URL
      window.history.replaceState({}, document.title, "/auth?tab=signin");
    } catch (error: any) {
      console.error("Password update exception:", error);
      toast({
        title: "Error updating password",
        description: error.message || "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };



  const headlineByTab = isPasswordUpdate
    ? { title: "Set a new password", subtitle: "Choose a strong password you'll remember." }
    : activeTab === "signup"
    ? { title: "Create your Hound account", subtitle: "Get paid faster and grow your reviews." }
    : activeTab === "reset"
    ? { title: "Reset your password", subtitle: "We'll verify it's you with your security question." }
    : { title: "Welcome back", subtitle: "Sign in to access your Hound dashboard." };

  return (
    <div className="min-h-screen flex flex-col bg-background relative">
      {/* Header */}
      <header className="sticky top-0 z-40 w-full bg-background/80 backdrop-blur border-b border-border">
        <div className="container flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 font-bold text-xl">
            Hound
          </Link>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="gap-1.5 bg-mint text-mint-foreground hover:bg-mint-dark"
              onClick={() => navigate("/")}
            >
              <Home className="h-4 w-4" />
              Home
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5"
              onClick={() => navigate("/help")}
            >
              <HelpCircle className="h-4 w-4" />
              Help
            </Button>
          </div>
        </div>
      </header>

      {/* Form panel */}
      <div className="flex-1 flex items-start justify-center px-4 sm:pt-16 pt-[40px] pb-10">
        <div className="w-full max-w-[420px] space-y-6">
          {/* Branding header */}
          <div className="flex flex-col items-center gap-3 pb-2">
            <span className="text-3xl font-extrabold tracking-tight">Hound</span>
            <p className="text-sm text-muted-foreground text-center">
              Payments, receipts & reviews — all in one place.
            </p>
          </div>

          <div className="space-y-1.5">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{headlineByTab.title}</h1>
            <p className="text-sm text-muted-foreground">{headlineByTab.subtitle}</p>
          </div>

          <Tabs
            value={activeTab}
            onValueChange={(value) => {
              if (value !== "signup") signupInProgressRef.current = false;
              setActiveTab(value);
            }}
            className="w-full"
          >
            {!isPasswordUpdate && activeTab !== "reset" && !HIDE_PAYWALL && (
              <TabsList className="w-full bg-transparent border-b rounded-none p-0 h-auto justify-start gap-6">
                <TabsTrigger
                  value="signin"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-mint data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none px-0 pb-3 font-medium"
                >
                  Sign In
                </TabsTrigger>
                <TabsTrigger
                  value="signup"
                  onClick={(e) => { e.preventDefault(); navigate("/signup"); }}
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-mint data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none px-0 pb-3 font-medium"
                >
                  Sign Up
                </TabsTrigger>
              </TabsList>
            )}
          
          <TabsContent value="signin" className="space-y-4 mt-4">
            {new URLSearchParams(location.search).get("needs_subscription") === "1" && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  <div className="font-medium mb-1">Payment not completed</div>
                  <p>
                    Your account exists but the subscription payment didn't go through.
                    Sign in below — you'll be taken straight to the card form to finish activating your account.
                  </p>
                </AlertDescription>
              </Alert>
            )}
            <form onSubmit={handleEmailSignIn} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input 
                  id="email" 
                  type="email" 
                  placeholder="you@example.com" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input 
                    id="password" 
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••" 
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-500" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-500" />
                    )}
                  </Button>
                </div>
              </div>
              <div className="flex justify-end -mt-1">
                <Button
                  variant="link"
                  type="button"
                  className="px-0 h-auto text-xs text-muted-foreground hover:text-mint-dark"
                  onClick={() => setActiveTab("reset")}
                >
                  Forgot password?
                </Button>
              </div>
              <Button type="submit" className="w-full h-11 font-semibold" disabled={loading}>
                {loading ? "Signing in..." : "Sign In"}
              </Button>
            </form>
          </TabsContent>
          
          <TabsContent value="signup" className="space-y-4 mt-4">
            {HIDE_PAYWALL ? (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  <div className="font-medium mb-1">Create your account on our website</div>
                  <p className="text-muted-foreground">
                    To sign up for Hound, please visit{" "}
                    <span className="font-medium text-foreground">houndpayments.co.uk</span>{" "}
                    from a browser. Once your account is active, sign in here.
                  </p>
                </AlertDescription>
              </Alert>
            ) : null}
            {!HIDE_PAYWALL && (<>


            {signupComplete ? (
              <Alert className="bg-mint/20 border-mint">
                <CheckCircle className="h-4 w-4 text-mint-dark" />
                <AlertDescription className="text-sm">
                  <div className="font-medium mb-2">Account created successfully!</div>
                  <p>You can now sign in with your credentials.</p>
                  <Button 
                    className="w-full mt-4" 
                    variant="outline"
                    onClick={() => {
                      setActiveTab("signin");
                      setSignupComplete(false);
                    }}
                  >
                    Go to Sign In
                  </Button>
                </AlertDescription>
              </Alert>
            ) : (
              <>
              <form onSubmit={handleEmailSignUp} className="space-y-4">
                <fieldset disabled={showCheckout} className="space-y-4 disabled:opacity-60">
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input 
                    id="signup-email" 
                    type="email" 
                    placeholder="you@example.com" 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Password</Label>
                  <div className="relative">
                    <Input 
                      id="signup-password" 
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••" 
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-500" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-500" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Password must be at least 6 characters long
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-confirm-password">Confirm Password</Label>
                  <div className="relative">
                    <Input
                      id="signup-confirm-password"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={6}
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-500" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-500" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-promo">Discount code (optional)</Label>
                  {promoApplied ? (
                    <div className="flex items-center justify-between rounded-md border border-mint bg-mint/10 px-3 py-2 text-sm">
                      <span>
                        <span className="font-semibold">{promoApplied.code}</span>
                        <span className="text-muted-foreground"> — {promoApplied.description}</span>
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => { setPromoApplied(null); setPromoInput(""); setPromoError(null); }}
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        id="signup-promo"
                        placeholder="Enter code"
                        value={promoInput}
                        onChange={(e) => { setPromoInput(e.target.value); setPromoError(null); }}
                        disabled={promoChecking}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        disabled={!promoInput.trim() || promoChecking}
                        onClick={async () => {
                          setPromoChecking(true);
                          setPromoError(null);
                          try {
                            const { data, error } = await supabase.functions.invoke("validate-promo-code", {
                              body: { code: promoInput.trim() },
                            });
                            if (error) throw error;
                            if (data?.valid) {
                              setPromoApplied({ code: data.code, description: data.description, isFullDiscount: data.is_full_discount });
                            } else {
                              setPromoError(data?.error || "Invalid code");
                            }
                          } catch (e: any) {
                            setPromoError(e?.message || "Could not check code");
                          } finally {
                            setPromoChecking(false);
                          }
                        }}
                      >
                        {promoChecking ? "Checking..." : "Apply"}
                      </Button>
                    </div>
                  )}
                  {promoError && <p className="text-xs text-destructive">{promoError}</p>}
                </div>

                </fieldset>

                {showCheckout && (
                  <div className="space-y-2 pt-2 border-t">
                    <div className="space-y-8">
                      <div>
                        <Label>Payment details</Label>
                        <p className="text-xs text-muted-foreground">
                          {signupPricingSentence({ isFullDiscount: promoApplied?.isFullDiscount, isHalfPromo: !!promoApplied && !promoApplied?.isFullDiscount })}
                          {" "}You cannot sign in until this step is finished.
                        </p>
                      </div>
                      <CustomSignupCheckout
                        email={email}
                        returnPath="/auth"
                        promoCode={promoApplied?.code}
                        isFullDiscount={promoApplied?.isFullDiscount}
                        isHalfPromo={!!promoApplied && !promoApplied?.isFullDiscount}
                        onError={(msg) =>
                          toast({ title: "Payment form error", description: msg, variant: "destructive" })
                        }
                      />
                    </div>
                  </div>
                )}

                {!showCheckout && (
                  <Button type="submit" className="w-full h-11 font-semibold" disabled={loading}>
                    {loading ? "Creating account..." : "Sign Up"}
                  </Button>
                )}

                {!showCheckout && (
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription className="text-xs text-muted-foreground">
                      {signupPricingSentence({ isFullDiscount: promoApplied?.isFullDiscount, isHalfPromo: !!promoApplied && !promoApplied?.isFullDiscount })}
                    </AlertDescription>
                  </Alert>
                )}
              </form>
              </>
            )}
            </>)}
          </TabsContent>
          
          <TabsContent value="reset" className="space-y-4 mt-4">
            {resetSent ? (
              <Alert>
                <Mail className="h-4 w-4" />
                <AlertDescription>
                  <div className="font-medium mb-1">Password reset email sent!</div>
                  <p>Check your email for the password reset link. You can close this page now.</p>
                  <p className="mt-2 text-xs text-muted-foreground">
                    If you don't see the email, check your spam folder.
                  </p>
                  <Button 
                    className="w-full mt-4" 
                    variant="outline"
                    onClick={() => {
                      setActiveTab("signin");
                      setResetSent(false);
                    }}
                  >
                    Back to Sign In
                  </Button>
                </AlertDescription>
              </Alert>
            ) : resetStep === "email" ? (
              <form onSubmit={handleResetLookup} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-email">Email</Label>
                  <Input
                    id="reset-email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <p className="text-sm text-muted-foreground">
                    We'll ask your security question to verify it's you.
                  </p>
                </div>
                <Button type="submit" className="w-full h-11 font-semibold" disabled={loading}>
                  {loading ? "Checking..." : "Continue"}
                </Button>
                <div className="text-center">
                  <Button
                    variant="link"
                    type="button"
                    className="px-0"
                    onClick={() => {
                      setActiveTab("signin");
                      setResetStep("email");
                      setResetQuestion(null);
                      setResetAnswer("");
                    }}
                  >
                    Back to Sign In
                  </Button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleResetVerify} className="space-y-4">
                <div className="space-y-2">
                  <Label>Security Question</Label>
                  <p className="text-sm font-medium">{resetQuestion}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reset-answer">Your Answer</Label>
                  <Input
                    id="reset-answer"
                    type="text"
                    value={resetAnswer}
                    onChange={(e) => setResetAnswer(e.target.value)}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Not case-sensitive.
                  </p>
                </div>
                <Button type="submit" className="w-full h-11 font-semibold" disabled={loading}>
                  {loading ? "Verifying..." : "Verify & Send Reset Link"}
                </Button>
                <div className="text-center">
                  <Button
                    variant="link"
                    type="button"
                    className="px-0"
                    onClick={() => {
                      setResetStep("email");
                      setResetQuestion(null);
                      setResetAnswer("");
                    }}
                  >
                    Use a different email
                  </Button>
                </div>
              </form>
            )}
          </TabsContent>

          <TabsContent value="update-password" className="space-y-4 mt-4">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Please enter your new password below.
              </AlertDescription>
            </Alert>
            
            <form onSubmit={handlePasswordUpdate} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <div className="relative">
                  <Input 
                    id="new-password" 
                    type={showNewPassword ? "text" : "password"}
                    placeholder="••••••••" 
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-500" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-500" />
                    )}
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <div className="relative">
                  <Input 
                    id="confirm-password" 
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="••••••••" 
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-500" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-500" />
                    )}
                  </Button>
                </div>
              </div>
              <Button type="submit" className="w-full h-11 font-semibold" disabled={loading}>
                {loading ? "Updating..." : "Update Password"}
              </Button>
            </form>
          </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default Auth;
